import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, BadgeCheck } from 'lucide-react';
import { getImageUrl } from '../services/api';

function InitialsAvatar({ name, className }) {
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const hue = Array.from(name || '').reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);
  return (
    <div
      className={className}
      style={{ background: `hsl(${hue}, 55%, 30%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '1.5em' }}
    >
      {initials}
    </div>
  );
}

function ArtistCard({ artist }) {
  const [imageError, setImageError] = useState(false);

  const formatFollowers = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  const hasValidImage = artist.image && !artist.image.includes('placehold.co');
  const imageUrl = getImageUrl(artist.image);
  const showFallback = imageError || !hasValidImage;

  return (
    <Link to={`/artists/${artist.id}`} className="group block">
      <div className="card hover-lift p-4 text-center h-full flex flex-col items-center">
        <div className="relative mx-auto w-20 h-20 md:w-32 md:h-32 mb-3 md:mb-5">
          {showFallback ? (
            <InitialsAvatar
              name={artist.name}
              className="w-full h-full rounded-full border-2 border-neutral-700 group-hover:border-yellow-500 transition-all duration-300"
            />
          ) : (
            <img
              src={imageUrl}
              alt={artist.name}
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
              className="w-full h-full object-cover rounded-full border-2 border-neutral-700 group-hover:border-yellow-500 transition-all duration-300 text-[10px] text-transparent"
            />
          )}
          {artist.verified && (
            <div className="absolute bottom-0 right-0 w-4 h-4 md:w-6 md:h-6 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
              <BadgeCheck className="w-3 h-3 md:w-4 md:h-4 text-black" />
            </div>
          )}
        </div>
        <h3 className="font-bold text-[11px] md:text-base text-white group-hover:text-yellow-500 transition-colors truncate">
          {artist.name}
        </h3>
        <p className="text-yellow-500 text-[9px] md:text-xs font-medium mb-1 md:mb-1.5">{artist.role}</p>
        <div className="flex items-center justify-center gap-1 text-neutral-400 text-[9px] md:text-[11px] mb-1.5 md:mb-2">
          <MapPin className="w-3 h-3 md:w-3.5 md:h-3.5" />
          <span className="truncate">{artist.location}</span>
        </div>
        <div className="flex items-center justify-center gap-1 text-neutral-400 text-[9px] md:text-[11px] font-medium pt-1.5 md:pt-2 border-t border-neutral-800/50">
          <Users className="w-3 h-3 md:w-3.5 md:h-3.5" />
          <span>{formatFollowers(artist.followers)}</span>
        </div>
      </div>
    </Link>
  );
}

export default ArtistCard;
